import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { useAuth } from "@/lib/AuthContext";

export default function ChangePassword() {
  const { t } = useTranslation();
  const { user, changePassword } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (next.length < 8) return setErr(t("changePassword.errTooShort"));
    if (next !== confirm) return setErr(t("changePassword.errMismatch"));
    if (next === current) return setErr(t("changePassword.errSame"));
    setBusy(true);
    try {
      await changePassword(current, next);
      // changePassword clears tokens; force a re-login.
      nav("/login", { replace: true });
    } catch (exc) {
      setErr(exc instanceof Error ? exc.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full flex items-center justify-center p-6">
      <form onSubmit={submit} className="card p-8 w-full max-w-sm space-y-5">
        <header>
          <p className="font-mono text-xs uppercase tracking-widest text-text-muted">
            mes · change password
          </p>
          <h1 className="text-3xl mt-2">{t("changePassword.title")}</h1>
          {user?.must_change_password ? (
            <p className="text-text-muted text-sm mt-2">
              {t("changePassword.firstLogin")}&nbsp;
              <span className="font-mono">{user.login}</span>.
            </p>
          ) : (
            <p className="text-text-muted text-sm mt-2">
              {t("changePassword.reLoginHint")}
            </p>
          )}
        </header>

        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            {t("changePassword.currentPassword")}
          </span>
          <input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            {t("changePassword.newPassword")}
          </span>
          <input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            {t("changePassword.confirm")}
          </span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border"
          />
        </label>

        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

        <button type="submit" className="btn btn-primary w-full" disabled={busy}>
          {busy ? t("changePassword.changing") : t("changePassword.submit")}
        </button>
      </form>
    </div>
  );
}
